import React, { useState, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Menu,
  RefinementList,
  useMenu,
  useRange,
  useRefinementList,
  useHierarchicalMenu,
  HierarchicalMenu,
} from 'react-instantsearch';
import { Modal, RangeSlider, OutsideClickHandler, IconCollection } from '../../components';
import {
  FILTER_CLICKED_EVENT,
  algoliaEventTrackFn,
  clickedFiltersMethodName,
} from './AlgoliaEvents';
import {
  ALOGOLIA_SEARCH_ATTRIBUTES,
  ALOGOLIA_SEARCH_COMPONENT,
  SEARCH_KEY_PARAMS_REFINEMENT,
} from '../../util/types';
import { getSubCategoryFilters } from '../../util/api';
import { getLocationByZipCode } from '../../util/maps';
import { businessCategories, marketplaceCategories } from '../../config/configListing';

import css from './AlgoliaSearchPage.module.css';
import classNames from 'classnames';

const {
  refinement_list,
  range_input,
  menu,
  menuSelect,
  customRefinementList,
  hierarchicalList,
  zipcode_text_field,
} = ALOGOLIA_SEARCH_COMPONENT;

const CustomRefinementList = props => {
  const {
    label,
    attribute,
    currentUser,
    algoliaState,
    setAlgoliaState,
    isMobileLayout,
    isNearMe,
    history,
    location,
    onClearFilter,
    ...rest
  } = props;
  const { items, refine } = useRefinementList({ attribute, limit: 50 });
  const [isOpen, setIsOpen] = useState(isMobileLayout ? true : !!props.isOpen);
  const [showAll, setShowAll] = useState(false);

  const filterTestId = `filter-${attribute}`;

  useEffect(() => {
    if (isMobileLayout) {
      setIsOpen(true);
    }
  }, [isMobileLayout]);

  const toggleBox = event => {
    if (isMobileLayout) return;
    event.stopPropagation();
    setIsOpen(prev => !prev);
  };

  const toggleShowAll = event => {
    event.stopPropagation();
    setShowAll(prev => !prev);
  };

  const searchkey = SEARCH_KEY_PARAMS_REFINEMENT[attribute];
  const isConverted =
    typeof algoliaState == 'object' && algoliaState[searchkey] ? algoliaState[searchkey] : '';

  const clearRefinements = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    const searchkey = SEARCH_KEY_PARAMS_REFINEMENT[attribute];
    if (typeof onClearFilter === 'function') {
      onClearFilter(searchkey || attribute);
    }

    // Iterate over all selected refinements and call `refine` with an empty value
    items.forEach(item => {
      if (item.isRefined) {
        refine(item.value); // This will remove the refinement by un-selecting the item
      }
    });

    // Update URL to remove the filter parameter
    if (history && location) {
      const currentParams = new URLSearchParams(location.search);

      // Remove the parameter from URL
      if (searchkey) {
        currentParams.delete(searchkey);
      }
      // Also try to remove the attribute name directly
      currentParams.delete(attribute);
      // Remove Algolia's refinementList format
      currentParams.delete(`refinementList[${attribute}]`);
      currentParams.delete(`refinementList[${attribute}][]`);

      const newUrl = `${location.pathname}?${currentParams.toString()}`;
      history.push(newUrl);
    }

    // Update algoliaState
    if (setAlgoliaState && algoliaState) {
      const newState = { ...algoliaState };
      if (searchkey) {
        delete newState[searchkey];
      }
      delete newState[attribute];
      setAlgoliaState(newState);
    }
  };

  // Sort items alphabetically, filtering out 'Custom' to place it at the end
  const itemsWithoutCustom = items.filter(st => st.value !== 'Custom');
  const customItems = items.filter(st => st.value === 'Custom');
  const sortedRegularItems = itemsWithoutCustom.sort((a, b) => a.value.localeCompare(b.value));
  const sortedItems = [...sortedRegularItems, ...customItems];

  const displayItems = isMobileLayout && !showAll ? sortedItems.slice(0, 3) : sortedItems;
  const hasMoreItems = sortedItems.length > 3;
  return !!(Array.isArray(items) && items.length) ? (
    <>
      <div className={css.spacer}></div>
      <div className={css.priceInput}>
        {/* <OutsideClickHandler onOutsideClick={() => setIsOpen(false)}> */}
        <button
          className={css.priceButton}
          onClick={toggleBox}
          data-testid={`${filterTestId}-toggle`}
        >
          <div className={css.label}>{label}</div>
          <div className={css.labelActions}>
            {isMobileLayout && hasMoreItems && (
              <span
                className={css.seeAllButton}
                onClick={toggleShowAll}
                data-testid={`${filterTestId}-see-all`}
              >
                {showAll ? 'See less' : 'See all'}
              </span>
            )}
            {!isMobileLayout && <span className={css.iconsFuntion}>{isOpen ? '-' : '+'}</span>}
          </div>
        </button>
        {isOpen ? (
          <>
            <ul className={css.priceDropDown}>
              {isNearMe && attribute == ALOGOLIA_SEARCH_ATTRIBUTES['pickShippingDetailsCity'] ? (
                <li key={'item.label'}>
                  <label>
                    <input
                      type="checkbox"
                      checked={isConverted == isNearMe}
                      onChange={() => {
                        refine(isNearMe);
                      }}
                    />
                    <div className={css.selectCategory}>
                      <span>{'Near Me'}</span>
                    </div>
                  </label>
                </li>
              ) : null}
              {displayItems
                .filter(
                  st =>
                    !(
                      isNearMe &&
                      attribute == ALOGOLIA_SEARCH_ATTRIBUTES['pickShippingDetailsCity'] &&
                      isNearMe == st.label
                    )
                )
                .map(item => (
                  <li key={item.label}>
                    <label
                      data-testid={`${filterTestId}-option-${String(item.value || item.label)
                        .toLowerCase()
                        .replace(/\s+/g, '-')}`}
                    >
                      <input
                        type="checkbox"
                        checked={item.isRefined}
                        onChange={() => {
                          const wasRefined = item.isRefined;
                          refine(item.value);

                          // When unselecting, update the URL to remove the filter
                          if (wasRefined && history && location) {
                            const currentParams = new URLSearchParams(location.search);
                            const key = SEARCH_KEY_PARAMS_REFINEMENT[attribute];
                            if (key) {
                              const existing = currentParams.getAll(key);
                              currentParams.delete(key);
                              existing
                                .filter(v => v !== item.value)
                                .forEach(v => currentParams.append(key, v));
                            }
                            history.push(`${location.pathname}?${currentParams.toString()}`);
                          }

                          const userID = currentUser?.id?.uuid;
                          if (userID && item.value) {
                            algoliaEventTrackFn(clickedFiltersMethodName, {
                              index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
                              eventName: FILTER_CLICKED_EVENT,
                              userToken: userID,
                              filters: [`${attribute}:${item.value}`],
                              eventData: {
                                filterValue: item.value,
                                userID,
                                timestamp: new Date().toISOString(),
                              },
                            });
                          }
                        }}
                      />
                      <div className={css.selectCategory}>
                        <span>{item.label}</span>
                        {isMobileLayout ||
                        ['Business', 'Individual'].includes(item.value) ? null : (
                          <span className={css.count}> ({item.count})</span>
                        )}
                      </div>
                    </label>
                  </li>
                ))}
            </ul>
            {isMobileLayout ? null : (
              <button type="button" className={css.clearButton} onClick={clearRefinements}>
                Clear
              </button>
            )}
          </>
        ) : null}
        {/* </OutsideClickHandler> */}
      </div>
    </>
  ) : null;
};

const SelectFilter = props => {
  const { label, attribute } = props;
  const { currentUser, algoliaState, ...rest } = props;
  const { items, refine } = useMenu({ ...rest });
  const { value: selectedValue } = items.find(item => item.isRefined) || {
    value: '',
  };

  const searchkey = SEARCH_KEY_PARAMS_REFINEMENT[attribute];
  const isConverted =
    typeof algoliaState == 'object' && algoliaState[searchkey] ? algoliaState[searchkey] : '';

  return (
    <>
      <div className={css.spacer}></div>
      <select
        value={selectedValue || isConverted}
        onChange={event => {
          refine(event.target.value);
        }}
      >
        <option selected value={''}>
          {' '}
          {label}{' '}
        </option>
        {items.map(item => (
          <option value={item.value}>
            {item.label} ({item.count})
          </option>
        ))}
      </select>
    </>
  );
};

export const SelectSubCategoryFilter = props => {
  const {
    label,
    attribute = 'publicData.subCategoryName',
    history,
    location,
    isMobileLayout,
    parentCategoryMapping,
  } = props;
  const { currentUser } = props;

  // Use Algolia's native useMenu hook - automatically updates based on current search state
  const { items, refine } = useMenu({
    attribute,
    limit: 100,
    sortBy: ['name:asc'],
  });

  const [isOpen, setIsOpen] = useState(false);

  // Get current subcategory from URL
  const getCurrentSubCategory = () => {
    const urlParams = new URLSearchParams(location.search);
    return urlParams.get('subCategoryName') || '';
  };

  // Find the currently selected item - prioritize URL parameter over Algolia refinement
  const urlSubCategory = getCurrentSubCategory();
  const selectedItem = items.find(item => item.isRefined);
  const selectedSubCategory = urlSubCategory || selectedItem?.value || '';

  // Function to get parent category for a given subcategory
  // Works with both businessCategories and marketplaceCategories from config
  const getParentCategoryForSubcategory = subcategoryName => {
    // Check in marketplaceCategories first (Equipment, For Riding, etc.)
    for (const category of marketplaceCategories) {
      if (category.childCategories) {
        const found = category.childCategories.find(
          child => child.label === subcategoryName || child.key === subcategoryName
        );
        if (found) {
          return category.label;
        }
      }
    }

    // Then check in businessCategories (Boarding Services, Training & Lessons, etc.)
    for (const category of businessCategories) {
      if (category.childCategories) {
        const found = category.childCategories.find(
          child => child.label === subcategoryName || child.key === subcategoryName
        );
        if (found) {
          return category.label;
        }
      }
    }

    // If not found in either, return null
    return null;
  };

  const handleSubCategoryChange = value => {
    setIsOpen(false);

    // Update URL with subCategoryName parameter
    const currentParams = new URLSearchParams(location.search);

    if (!value) {
      currentParams.delete('subCategoryName');
      currentParams.delete('hierarchicalCategories'); // Also clear parent category
      // Clear refinement by refining the currently selected item (toggles it off)
      if (selectedItem) {
        refine(selectedItem.value);
      }
    } else {
      currentParams.set('subCategoryName', value);

      // Automatically set the parent category in hierarchicalCategories
      // This will make the parent category appear selected/bold in mobile hamburger
      const parentCategory = getParentCategoryForSubcategory(value);
      if (parentCategory) {
        currentParams.set('hierarchicalCategories', parentCategory);
      }

      // Find the item and refine it in Algolia
      const itemToRefine = items.find(item => item.value === value);
      if (itemToRefine && !itemToRefine.isRefined) {
        refine(itemToRefine.value);
      }
    }

    // Navigate to the updated URL
    const newUrl = `${location.pathname}?${currentParams.toString()}`;
    if (history && typeof history.push === 'function') {
      history.push(newUrl);
    }
  };

  const handleNativeChange = event => {
    handleSubCategoryChange(event.target.value);
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const getSelectedLabel = () => {
    if (!selectedSubCategory) return `${label}: All`;
    const item = items.find(item => item.value === selectedSubCategory);
    return item ? `${item.label} (${item.count})` : `${label}: All`;
  };

  // Filter items to only show those with count > 0
  const itemsWithData = items.filter(item => item.count > 0);

  // If no items available, don't render the filter
  if (itemsWithData.length === 0) {
    return null;
  }

  // Custom dropdown for mobile
  if (isMobileLayout) {
    return (
      <>
        <div className={css.spacer}></div>
        <OutsideClickHandler
          onOutsideClick={() => setIsOpen(false)}
          className={css.outsideClickHandler}
        >
          <div className={css.customDropdownWrapper}>
            <button className={css.customDropdownButton} onClick={toggleDropdown} type="button">
              <span>{getSelectedLabel()}</span>
              <span className={classNames(css.dropdownArrow, isOpen && css.open)}>
                <IconCollection name="arrowDown" />
              </span>
            </button>
            {isOpen && (
              <div className={css.customDropdownMenu}>
                <div className={css.customDropdownItem} onClick={() => handleSubCategoryChange('')}>
                  {label}: All
                </div>
                {itemsWithData.map(item => (
                  <div
                    key={item.value}
                    className={`${css.customDropdownItem} ${
                      selectedSubCategory === item.value ? css.selected : ''
                    }`}
                    onClick={() => handleSubCategoryChange(item.value)}
                  >
                    {item.label} ({item.count})
                  </div>
                ))}
              </div>
            )}
          </div>
        </OutsideClickHandler>
      </>
    );
  }

  // Native select for desktop
  return (
    <>
      <div className={css.spacer}></div>
      <select value={selectedSubCategory} onChange={handleNativeChange}>
        <option value=""> {label}: All </option>
        {itemsWithData.map(item => (
          <option key={item.value} value={item.value}>
            {item.label} ({item.count})
          </option>
        ))}
      </select>
      <IconCollection name="arrowDown" />
    </>
  );
};

// Mobile dropdown for top-level categories using categoryLevels
export const SelectTopCategoryDropdown = props => {
  const { label = 'Category', isMobileLayout = true, history, location } = props;
  // Use categoryLevels attribute for simplified filtering
  const attribute = ALOGOLIA_SEARCH_ATTRIBUTES['categoryLevels'];

  const { items, refine } = useRefinementList({ attribute });

  const [isOpen, setIsOpen] = useState(false);

  // Read current selection from URL so dropdown reflects current state
  const selectedFromUrl = (() => {
    try {
      if (location) {
        const params = new URLSearchParams(location.search);
        return params.get('categoryLevels') || '';
      }
    } catch {}
    return '';
  })();

  // Build a simple flat list
  const topItems = Array.isArray(items) ? items : [];

  const selectedTop =
    topItems.find(i => i.isRefined) || topItems.find(i => i.value === selectedFromUrl);
  const selectedLabel = selectedTop
    ? `${selectedTop.label} (${selectedTop.count})`
    : `${label}: All`;

  const toggleDropdown = () => setIsOpen(prev => !prev);

  // If URL has a selection but InstantSearch hasn't refined yet (e.g., first render), refine to match URL
  useEffect(() => {
    try {
      if (
        selectedFromUrl &&
        Array.isArray(topItems) &&
        topItems.length &&
        !topItems.some(i => i.isRefined)
      ) {
        refine(selectedFromUrl);
      }
    } catch {}
  }, [selectedFromUrl, topItems.length]);
  if (isMobileLayout) {
    return (
      <>
        <div className={css.spacer}></div>
        <OutsideClickHandler
          onOutsideClick={() => setIsOpen(false)}
          className={css.outsideClickHandler}
        >
          <div className={css.customDropdownWrapper}>
            <button className={css.customDropdownButton} onClick={toggleDropdown} type="button">
              <span>{selectedLabel}</span>
              <span className={classNames(css.dropdownArrow, isOpen && css.open)}>
                <IconCollection name="arrowDown" />
              </span>
            </button>
            {isOpen && (
              <div className={css.customDropdownMenu}>
                <div
                  className={css.customDropdownItem}
                  onClick={() => {
                    // Clear all refinements for this attribute
                    topItems.forEach(item => {
                      if (item.isRefined) {
                        refine(item.value);
                      }
                    });
                    // Remove query param from URL if router props available
                    try {
                      if (history && location) {
                        const params = new URLSearchParams(location.search);
                        params.delete('categoryLevels');
                        history.push(`${location.pathname}?${params.toString()}`);
                      }
                    } catch {}
                    setIsOpen(false);
                  }}
                >
                  {label}: All
                </div>
                {topItems.map(item => (
                  <div
                    key={item.value}
                    className={`${css.customDropdownItem} ${item.isRefined ? css.selected : ''}`}
                    onClick={() => {
                      // Clear all other selections first, then select this one
                      topItems.forEach(otherItem => {
                        if (otherItem.isRefined && otherItem.value !== item.value) {
                          refine(otherItem.value);
                        }
                      });
                      // Now toggle this item (if not already refined, it will be refined)
                      if (!item.isRefined) {
                        refine(item.value);
                      }
                      setIsOpen(false);
                    }}
                  >
                    {item.label} ({item.count})
                  </div>
                ))}
              </div>
            )}
          </div>
        </OutsideClickHandler>
      </>
    );
  }

  // Fallback simple select for desktop if ever used
  return (
    <>
      <div className={css.spacer}></div>
      <select
        value={selectedTop ? selectedTop.value : ''}
        onChange={e => {
          const newValue = e.target.value;
          // Clear all current selections
          topItems.forEach(item => {
            if (item.isRefined) {
              refine(item.value);
            }
          });
          // Apply new selection if not empty
          if (newValue) {
            refine(newValue);
          }
        }}
      >
        <option value="">{label}: All</option>
        {topItems.map(item => (
          <option key={item.value} value={item.value}>
            {item.label} ({item.count})
          </option>
        ))}
      </select>
      <IconCollection name="arrowDown" />
    </>
  );
};

function stripLeadingZeroFromInput(value = '') {
  return value.toString().replace(/^(0+)\d/, part => Number(part).toString());
}

const unsetNumberInputValue = '';
function PrinceRangeInput(props) {
  const { attribute, initialCategory, searchId, isMobileLayout } = props;
  const uniqueId = `price-range-${searchId || 'default'}`;
  const { start, range, canRefine, precision, refine } = useRange({ attribute });
  const [isOpen, setIsOpen] = useState(isMobileLayout ? true : !!props.isOpen);
  const step = 1 / Math.pow(10, precision || 0);
  const values = {
    min: start[0] !== -Infinity && start[0] !== range.min ? start[0] : unsetNumberInputValue,
    max: start[1] !== Infinity && start[1] !== range.max ? start[1] : unsetNumberInputValue,
  };
  const [prevValues, setPrevValues] = useState(values);

  const [{ from, to }, setRange] = useState({
    from: values.min?.toString(),
    to: values.max?.toString(),
  });

  if (values.min !== prevValues.min || values.max !== prevValues.max) {
    setRange({ from: values.min?.toString(), to: values.max?.toString() });
    setPrevValues(values);
  }

  const handleSliderChange = sliderValues => {
    const [min, max] = sliderValues;
    setRange({ from: min.toString(), to: max.toString() });
    refine([min ? Number(min) : undefined, max ? Number(max) : undefined]);
  };

  const validHandles =
    range && range.min
      ? [
          Math.max(Number(from), range.min) || range.min, // Limit `from` to at least `range.min`
          Math.min(Number(to), range.max) || range.max, // Limit `to` to at most `range.max`
        ]
      : [];

  return (
    <>
      <div className={css.spacer}></div>
      <form
        onSubmit={event => {
          event.preventDefault();
          refine([from ? Number(from) : undefined, to ? Number(to) : undefined]);
        }}
        className={css.rangeFilter}
      >
        <div
          onClick={e => {
            if (isMobileLayout) return;
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
          className={css.labelHeading}
          data-testid={`${uniqueId}-toggle`}
        >
          <label className={css.label}>
            <span>Price (USD)</span>
          </label>
          {!isMobileLayout && <span className={css.iconsFuntion}>{isOpen ? '-' : '+'}</span>}
        </div>
        {isOpen && (
          <div className={css.rangeFilterFieldsWrapper}>
            <div className={css.rangeFields}>
              <label>
                <input
                  type="number"
                  min={range.min}
                  max={range.max}
                  value={stripLeadingZeroFromInput(from || unsetNumberInputValue)}
                  step={step}
                  placeholder={range.min?.toString()}
                  disabled={!canRefine}
                  data-testid={`${uniqueId}-min-input`}
                  onInput={({ currentTarget }) => {
                    const value = currentTarget.value;
                    setRange({ from: value || unsetNumberInputValue, to });
                  }}
                  onBlur={e => {
                    e.preventDefault();
                    refine([from ? Number(from) : undefined, to ? Number(to) : undefined]);
                  }}
                />
              </label>
              <span>
                <FormattedMessage id="RangeInputFilter.to" />
              </span>
              <label>
                <input
                  type="number"
                  min={range.min}
                  max={range.max}
                  value={stripLeadingZeroFromInput(to || unsetNumberInputValue)}
                  step={step}
                  placeholder={range.max?.toString()}
                  disabled={!canRefine}
                  data-testid={`${uniqueId}-max-input`}
                  onInput={({ currentTarget }) => {
                    const value = currentTarget.value;
                    setRange({ from, to: value || unsetNumberInputValue });
                  }}
                  onBlur={e => {
                    e.preventDefault();
                    refine([from ? Number(from) : undefined, to ? Number(to) : undefined]);
                  }}
                />
              </label>
            </div>

            <RangeSlider
              min={range.min}
              max={range.max}
              step={step}
              handles={validHandles}
              onChange={handleSliderChange}
            />
            {/* {error && <span className={css.error}>{error}</span>} */}
            {/* <Button className={css.applyRange} type="submit"><FormattedMessage id='RangeInputFilter.apply' /></Button> */}
            {isMobileLayout ? null : (
              <div
                className={css.clearButton}
                onClick={e => {
                  refine([range.min, range.max]);
                }}
              >
                Clear
              </div>
            )}
          </div>
        )}
        {/* Uncomment the button if necessary */}
      </form>
    </>
  );
}

const ZipcodeSearchField = props => {
  const {
    label,
    isMobileLayout,
    setAlgoliaState,
    algoliaState,
    onZipcodeSearch,
    initialZipcode,
    closeMobileFilters,
  } = props;
  const [zipcode, setZipcode] = useState(initialZipcode || '');
  const [isOpen, setIsOpen] = useState(isMobileLayout ? true : !!props.isOpen);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Update zipcode when initialZipcode changes
  useEffect(() => {
    if (initialZipcode) {
      setZipcode(initialZipcode);
    }
  }, [initialZipcode]);

  // Removed auto-search on change for mobile; user must click Search (or press Enter)

  const toggleBox = event => {
    if (isMobileLayout) return;
    event.stopPropagation();
    setIsOpen(prev => !prev);
  };

  const handleZipcodeChange = e => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setZipcode(value);
    setError('');
  };

  const handleSearch = async () => {
    if (!zipcode || (zipcode.length !== 4 && zipcode.length !== 5)) {
      setError('Please enter a valid 4 or 5-digit zipcode');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const locationData = await getLocationByZipCode(zipcode, 'US');

      if (
        locationData &&
        !locationData.error &&
        locationData.origin &&
        locationData.origin.lat &&
        locationData.origin.lng
      ) {
        if (onZipcodeSearch) {
          onZipcodeSearch(zipcode, locationData);
        }

        // Auto-switch to Business when zipcode filter is applied (mobile only)
        if (isMobileLayout && history && location && typeof history.push === 'function') {
          const currentParams = new URLSearchParams(location.search);
          currentParams.set('currentUserType', 'Business');
          const newUrl = `${location.pathname}?${currentParams.toString()}`;
          history.push(newUrl);
        }

        // Close mobile filters after successful search
        if (isMobileLayout && typeof closeMobileFilters === 'function') {
          closeMobileFilters();
        }
      } else {
        setError('Invalid zipcode. Please try again.');
      }
    } catch (err) {
      console.error('Error fetching location:', err);
      setError('Unable to find location. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setZipcode('');
    setError('');
    if (onZipcodeSearch) {
      onZipcodeSearch('', null);
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <>
      <div className={css.spacer}></div>
      <div className={css.priceInput}>
        <button className={css.priceButton} onClick={toggleBox}>
          <div className={css.label}>{label || 'Near Me (Zipcode)'}</div>
          <div className={css.labelActions}>
            {!isMobileLayout && <span className={css.iconsFuntion}>{isOpen ? '-' : '+'}</span>}
          </div>
        </button>
        {isOpen && (
          <>
            <div className={css.zipcodeSearchWrapper}>
              <div className={css.zipcodeInputGroup}>
                <div>
                  <label>Enter ZIP</label>
                  <input
                    type="text"
                    className={css.zipcodeInput}
                    placeholder="12345"
                    value={zipcode}
                    onChange={handleZipcodeChange}
                    onKeyPress={handleKeyPress}
                    maxLength={6}
                  />
                </div>
                <button
                  className={css.zipcodeSearchButton}
                  onClick={handleSearch}
                  disabled={loading || (zipcode.length !== 4 && zipcode.length !== 5)}
                >
                  Confirm
                </button>
              </div>
              {error && <div className={css.zipcodeError}>{error}</div>}
              {/* {zipcode && !error && !loading && (
                <div className={css.zipcodeInfo}>Showing results within 50 miles of {zipcode}</div>
              )} */}
            </div>
            {zipcode && (
              <div className={css.clearButton} onClick={handleClear}>
                Clear
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

function CustomHierarchicalMenu(props) {
  const {
    label,
    attribute,
    currentUser,
    algoliaState,
    setAlgoliaState,
    isMobileLayout,
    history,
    location,
    ...rest
  } = props;
  const {
    items,
    refine,
    canToggleShowMore,
    toggleShowMore,
    isShowingMore,
    createURL,
  } = useHierarchicalMenu({ attributes: attribute });
  const [isOpen, setIsOpen] = useState(isMobileLayout ? true : !!props.isOpen);
  const [showAll, setShowAll] = useState(false);
  const [selectedMainCategory, setSelectedMainCategory] = useState(null);

  const toggleList = () => {
    if (isMobileLayout) return;
    setIsOpen(prev => !prev);
  };

  const toggleShowAll = event => {
    event.stopPropagation();
    setShowAll(prev => !prev);
  };

  const clearCategoryRefinement = e => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    refine('');

    if (history && location) {
      const params = new URLSearchParams(location.search);
      params.delete('hierarchicalCategories');
      params.delete('subCategoryName');
      params.delete('category');
      params.delete('categoryLevels');
      params.delete('menu[categoryLevels]');
      params.delete('refinementList[categoryLevels]');
      params.delete('refinementList[categoryLevels][]');
      const queryString = params.toString();
      history.push(queryString ? `${location.pathname}?${queryString}` : location.pathname);
    }

    if (setAlgoliaState && algoliaState) {
      const nextState = { ...algoliaState };
      delete nextState.hierarchicalCategories;
      delete nextState.subCategoryName;
      delete nextState.category;
      delete nextState.categoryLevels;
      setAlgoliaState(nextState);
    }
  };

  useEffect(() => {
    if (isMobileLayout) {
      setIsOpen(true);
    }
  }, [isMobileLayout]);

  // Find currently selected main category from refined items
  useEffect(() => {
    if (isMobileLayout && items.length > 0) {
      const refinedItem = items.find(item => item.isRefined);
      if (refinedItem) {
        setSelectedMainCategory(refinedItem.value);
      } else {
        // Check if any subcategory is selected
        const itemWithRefinedChild = items.find(
          item => item.data && item.data.some(child => child.isRefined)
        );
        if (itemWithRefinedChild) {
          setSelectedMainCategory(itemWithRefinedChild.value);
        }
      }
    }
  }, [items, isMobileLayout]);

  // Reset selected main category when top-level category changes
  useEffect(() => {
    if (isMobileLayout) {
      setSelectedMainCategory(null);
    }
  }, [algoliaState?.categoryLevels, isMobileLayout]);

  // Filter items based on selected top-level category (categoryLevels) for mobile
  const getFilteredItems = () => {
    // Check if there's a selected categoryLevels filter from the top dropdown
    const urlParams = new URLSearchParams(window.location.search);

    // Check multiple possible parameter names that Algolia might use
    const selectedCategoryLevel =
      urlParams.get('categoryLevels') ||
      urlParams.get('refinementList[categoryLevels]') ||
      urlParams.get('menu[categoryLevels]') ||
      urlParams.get('refinementList%5BcategoryLevels%5D'); // URL encoded version

    // Also check if any categoryLevels refinement is active in the current algolia state
    const algoliaRefinement = algoliaState?.categoryLevels;

    // Check if any item in the categoryLevels refinement list is selected
    let refinementListSelection = null;
    try {
      // Check for Algolia's refinement list format
      const refinementListParam =
        urlParams.get('refinementList[categoryLevels][]') ||
        urlParams.get('refinementList%5BcategoryLevels%5D%5B%5D');
      if (refinementListParam) {
        refinementListSelection = refinementListParam;
      }
    } catch (e) {
      console.log('Error parsing refinement list:', e);
    }

    const finalSelectedCategory =
      selectedCategoryLevel || algoliaRefinement || refinementListSelection;

    // Also check if we can detect selection from the items themselves
    const refinedTopLevelItem = items.find(item => item.isRefined);
    const detectedCategory = refinedTopLevelItem?.value;

    const actualSelectedCategory = finalSelectedCategory || detectedCategory;

    // ALWAYS filter when a category is selected, regardless of current refinements
    if (actualSelectedCategory) {
      // Find the matching category item
      let filtered = items.filter(item => item.value === actualSelectedCategory);

      // If the filtered result is empty but we have a selected category,
      // it might be because the hierarchical refinement changed the items structure
      // In this case, we need to reconstruct the filtered view
      if (filtered.length === 0 && items.length > 0) {
        // Check if any item has the selected category as a refined subcategory
        const itemWithRefinedSub = items.find(
          item =>
            item.data &&
            item.data.some(sub => sub.isRefined && item.value === finalSelectedCategory)
        );

        if (itemWithRefinedSub) {
          filtered = [itemWithRefinedSub];
        }
      }

      // If still no filtered results, but we detected a category, try to find it by checking all items
      if (filtered.length === 0 && actualSelectedCategory) {
        console.log('🔍 Trying alternative detection for:', actualSelectedCategory);
        // Look for any item that matches the category name
        const alternativeMatch = items.find(
          item =>
            item.value === actualSelectedCategory ||
            item.label === actualSelectedCategory ||
            (item.data && item.data.some(sub => sub.value.includes(actualSelectedCategory)))
        );
        if (alternativeMatch) {
          filtered = [alternativeMatch];
          console.log('✅ Found alternative match:', alternativeMatch.value);
        }
      }

      return filtered;
    }

    return items; // Show all if no top-level category is selected
  };

  // Sort items alphabetically, filtering out 'Custom' to place it at the end
  const itemsWithoutCustom = items.filter(item => item.value !== 'Custom');
  const customItems = items.filter(item => item.value === 'Custom');
  const sortedRegularItems = itemsWithoutCustom.sort((a, b) => a.value.localeCompare(b.value));
  const displayItems = [...sortedRegularItems, ...customItems];

  // Mobile-specific rendering - now using same HierarchicalList as desktop
  if (isMobileLayout) {
    return (
      <>
        <div className={css.spacer}></div>
        <div className={css.labelHeading} onClick={toggleList}>
          <label className={css.label}>{label}</label>
          <div className={css.labelActions}>
            {!isMobileLayout && <span className={css.iconsFuntion}>{isOpen ? '-' : '+'}</span>}
          </div>
        </div>
        {isOpen && (
          <>
            <HierarchicalList
              items={displayItems}
              onNavigate={refine}
              createURL={createURL}
              level={1}
              currentUser={currentUser}
              attribute={attribute[0]}
              allAttribute={attribute}
              isMobileLayout={isMobileLayout}
            />
            <div className={css.clearButton} onClick={clearCategoryRefinement}>
              Clear
            </div>
          </>
        )}
      </>
    );
  }

  // Desktop rendering
  const desktopDisplayItems = displayItems;

  return (
    <>
      <div className={css.spacer}></div>
      <div className={css.labelHeading} onClick={toggleList}>
        <label className={css.label}>{label}</label>
        <div className={css.labelActions}>
          {isMobileLayout && hasMoreItems && (
            <span className={css.seeAllButton} onClick={toggleShowAll}>
              {showAll ? 'See less' : 'See all'}
            </span>
          )}
          {!isMobileLayout && <span className={css.iconsFuntion}>{isOpen ? '-' : '+'}</span>}
        </div>
      </div>
      {isOpen && (
        <HierarchicalList
          items={desktopDisplayItems}
          onNavigate={refine}
          createURL={createURL}
          level={1}
          currentUser={currentUser}
          attribute={attribute[0]}
          allAttribute={attribute}
          isMobileLayout={isMobileLayout}
        />
      )}
      {props.showMore && (
        <button disabled={!props.canToggleShowMore} onClick={props.toggleShowMore}>
          {props.isShowingMore ? 'Show less' : 'Show more'}
        </button>
      )}

      {isMobileLayout ? null : (
        <button className={css.clearButton} onClick={clearCategoryRefinement}>
          Clear
        </button>
      )}
    </>
  );
}
// Mobile-specific hierarchical list component
function MobileHierarchicalList({
  items,
  createURL,
  onNavigate,
  currentUser,
  attribute,
  allAttribute,
  selectedMainCategory,
  setSelectedMainCategory,
  history,
  location,
  isMobileLayout,
}) {
  if (items.length === 0) {
    return null;
  }

  function isModifierClick(event) {
    const isMiddleClick = event.button === 1;
    return Boolean(
      isMiddleClick || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
    );
  }

  const handleSubCategoryClick = (subItem, event) => {
    if (isModifierClick(event)) {
      return;
    }
    event.preventDefault();

    onNavigate(subItem.value);

    // Auto-switch to Business when hierarchical category is selected (mobile only)
    if (isMobileLayout && history && location && typeof history.push === 'function') {
      const currentParams = new URLSearchParams(location.search);
      currentParams.set('currentUserType', 'Business');
      const newUrl = `${location.pathname}?${currentParams.toString()}`;
      history.push(newUrl);
    }

    // Track analytics
    const userID = currentUser?.id?.uuid;
    if (userID && subItem.value) {
      algoliaEventTrackFn(clickedFiltersMethodName, {
        index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
        eventName: FILTER_CLICKED_EVENT,
        userToken: userID,
        filters: [`${attribute}:${subItem.value}`],
        eventData: {
          filterValue: subItem.value,
          userID,
          timestamp: new Date().toISOString(),
        },
      });
    }
  };

  // Check if we have a filtered category (from top-level selection)
  const urlParams = new URLSearchParams(window.location.search);
  const selectedCategoryLevel =
    urlParams.get('categoryLevels') ||
    urlParams.get('refinementList[categoryLevels]') ||
    urlParams.get('menu[categoryLevels]') ||
    urlParams.get('refinementList%5BcategoryLevels%5D') ||
    urlParams.get('refinementList[categoryLevels][]') ||
    urlParams.get('refinementList%5BcategoryLevels%5D%5B%5D');

  // Also check if any item is refined (selected)
  const refinedItem = items.find(item => item.isRefined);
  const detectedCategoryFromItems = refinedItem?.value;

  const finalSelectedCategoryLevel = selectedCategoryLevel || detectedCategoryFromItems;

  // If a top-level category is selected, show its subcategories directly
  if (finalSelectedCategoryLevel) {
    // Find the main item that matches the selected category
    let mainItem = null;

    if (items.length === 1) {
      mainItem = items[0];
    } else if (items.length > 1) {
      // If we have multiple items, find the one that matches the selected category
      mainItem = items.find(item => item.value === finalSelectedCategoryLevel);
    }

    if (mainItem) {
      // Check if we have subcategories to show
      if (mainItem.data && mainItem.data.length > 0) {
        // ALWAYS show subcategories directly when a category is selected from top dropdown
        return (
          <>
            {/* Show parent category as clickable heading */}
            <div className={css.categoryHeading}>
              <h3>
                <a
                  href={createURL(mainItem.value)}
                  onClick={event => {
                    if (isModifierClick(event)) {
                      return;
                    }
                    event.preventDefault();
                    onNavigate(mainItem.value);

                    // Track analytics
                    const userID = currentUser?.id?.uuid;
                    if (userID && mainItem.value) {
                      algoliaEventTrackFn(clickedFiltersMethodName, {
                        index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
                        eventName: FILTER_CLICKED_EVENT,
                        userToken: userID,
                        filters: [`${attribute}:${mainItem.value}`],
                        eventData: {
                          filterValue: mainItem.value,
                          userID,
                          timestamp: new Date().toISOString(),
                        },
                      });
                    }
                  }}
                >
                  {mainItem.label}
                </a>
              </h3>
            </div>
            {/* Show subcategories */}
            <ul className={css.categoriesField}>
              {mainItem.data
                .sort((a, b) => a.value.localeCompare(b.value))
                .map(subItem => (
                  <li key={subItem.value}>
                    <a
                      href={createURL(subItem.value)}
                      onClick={event => handleSubCategoryClick(subItem, event)}
                      style={{ fontWeight: subItem.isRefined ? 'bold' : 'normal' }}
                      className={subItem.isRefined ? css.refinedItem : ''}
                    >
                      <span>
                        {subItem.label
                          .replace(`${mainItem.label} > `, '')
                          .replace(/lvl\d+\s*>\s*/g, '')
                          .trim()}
                      </span>
                      <span> ({subItem.count})</span>
                    </a>
                  </li>
                ))}
            </ul>
          </>
        );
      } else {
        // Look for subcategories in all items that match the selected category
        const allMatchingItems = items.filter(item => item.value === finalSelectedCategoryLevel);
        const allSubcategories = [];

        allMatchingItems.forEach(item => {
          if (item.data && item.data.length > 0) {
            allSubcategories.push(...item.data);
          }
        });

        if (allSubcategories.length > 0) {
          console.log(
            '✅ Found subcategories in alternative search:',
            allSubcategories.map(sub => sub.value)
          );
          return (
            <>
              {/* Show parent category as clickable heading */}
              <div className={css.categoryHeading}>
                <h3>
                  <a
                    href={createURL(mainItem.value)}
                    onClick={event => {
                      if (isModifierClick(event)) {
                        return;
                      }
                      event.preventDefault();
                      onNavigate(mainItem.value);

                      // Track analytics
                      const userID = currentUser?.id?.uuid;
                      if (userID && mainItem.value) {
                        algoliaEventTrackFn(clickedFiltersMethodName, {
                          index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
                          eventName: FILTER_CLICKED_EVENT,
                          userToken: userID,
                          filters: [`${attribute}:${mainItem.value}`],
                          eventData: {
                            filterValue: mainItem.value,
                            userID,
                            timestamp: new Date().toISOString(),
                          },
                        });
                      }
                    }}
                  >
                    {mainItem.label}
                  </a>
                </h3>
              </div>
              {/* Show subcategories */}
              <ul className={css.categoriesField}>
                {allSubcategories
                  .sort((a, b) => a.value.localeCompare(b.value))
                  .map(subItem => (
                    <li key={subItem.value}>
                      <a
                        href={createURL(subItem.value)}
                        onClick={event => handleSubCategoryClick(subItem, event)}
                        style={{ fontWeight: subItem.isRefined ? 'bold' : 'normal' }}
                        className={subItem.isRefined ? css.refinedItem : ''}
                      >
                        <span>
                          {subItem.label
                            .replace(`${mainItem.label} > `, '')
                            .replace(/lvl\d+\s*>\s*/g, '')
                            .trim()}
                        </span>
                        <span> ({subItem.count})</span>
                      </a>
                    </li>
                  ))}
              </ul>
            </>
          );
        }
        // If still no subcategories, show the main category itself
        return (
          <ul className={css.categoriesField}>
            <li key={mainItem.value}>
              <a
                href={createURL(mainItem.value)}
                onClick={event => {
                  if (isModifierClick(event)) {
                    return;
                  }
                  event.preventDefault();
                  onNavigate(mainItem.value);

                  // Track analytics
                  const userID = currentUser?.id?.uuid;
                  if (userID && mainItem.value) {
                    algoliaEventTrackFn(clickedFiltersMethodName, {
                      index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
                      eventName: FILTER_CLICKED_EVENT,
                      userToken: userID,
                      filters: [`${attribute}:${mainItem.value}`],
                      eventData: {
                        filterValue: mainItem.value,
                        userID,
                        timestamp: new Date().toISOString(),
                      },
                    });
                  }
                }}
                style={{ fontWeight: mainItem.isRefined ? 'bold' : 'normal' }}
                className={mainItem.isRefined ? css.refinedItem : ''}
              >
                <span>{mainItem.label}</span>
                <span> ({mainItem.count})</span>
              </a>
            </li>
          </ul>
        );
      }
    } else {
      console.log('❌ No matching main item found for:', finalSelectedCategoryLevel);
    }
  }

  // Show main categories when no top-level category is selected
  return (
    <ul className={css.categoriesField}>
      {items
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(item => (
          <li key={item.value}>
            <a
              href={createURL(item.value)}
              onClick={event => {
                if (isModifierClick(event)) {
                  return;
                }
                event.preventDefault();
                onNavigate(item.value);

                // Auto-switch to Business when hierarchical category is selected (mobile only)
                if (isMobileLayout && history && location && typeof history.push === 'function') {
                  const currentParams = new URLSearchParams(location.search);
                  currentParams.set('currentUserType', 'Business');
                  const newUrl = `${location.pathname}?${currentParams.toString()}`;
                  history.push(newUrl);
                }

                // Track analytics
                const userID = currentUser?.id?.uuid;
                if (userID && item.value) {
                  algoliaEventTrackFn(clickedFiltersMethodName, {
                    index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
                    eventName: FILTER_CLICKED_EVENT,
                    userToken: userID,
                    filters: [`${attribute}:${item.value}`],
                    eventData: {
                      filterValue: item.value,
                      userID,
                      timestamp: new Date().toISOString(),
                    },
                  });
                }
              }}
              style={{ fontWeight: item.isRefined ? 'bold' : 'normal' }}
              className={item.isRefined ? css.refinedItem : ''}
            >
              <span>{item.label}</span>
              <span> ({item.count})</span>
              {item.data && item.data.length > 0 && (
                <IconCollection name="sidenav_arrow" className={css.subcategoryArrow} />
              )}
            </a>
          </li>
        ))}
    </ul>
  );
}

function HierarchicalList({
  items,
  createURL,
  onNavigate,
  level,
  currentUser,
  attribute,
  allAttribute,
  isMobileLayout,
}) {
  const [showAllSubcategories, setShowAllSubcategories] = useState(false);
  const [selectedParentForMobile, setSelectedParentForMobile] = useState(null);

  if (items.length === 0) {
    return null;
  }

  function isModifierClick(event) {
    const isMiddleClick = event.button === 1;
    return Boolean(
      isMiddleClick || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
    );
  }

  const hierarchicalLevel = level || 0;
  const hierarchicalNextLevel = hierarchicalLevel + 1;

  const toggleShowAll = event => {
    event.preventDefault();
    event.stopPropagation();
    setShowAllSubcategories(prev => !prev);
  };

  // Mobile layout: Show all parent categories first, then subcategories at bottom
  if (isMobileLayout && level === 1) {
    const sortedItems = items.sort((a, b) => a.value.localeCompare(b.value));

    // Find the selected parent (if any)
    const selectedParent = sortedItems.find(
      item => item.isRefined || (item.data && item.data.some(sub => sub.isRefined))
    );
    const subcategories = selectedParent?.data || [];

    // Limit main categories to 5 items
    const mainCategoryLimit = 5;
    const shouldLimitMainCategories = sortedItems.length > mainCategoryLimit;
    const displayedMainCategories =
      shouldLimitMainCategories && !showAllSubcategories
        ? sortedItems.slice(0, mainCategoryLimit)
        : sortedItems;

    const subcategoryLimit = 4;
    const shouldLimitSubcategories = subcategories.length > subcategoryLimit;
    const displayedSubcategories =
      shouldLimitSubcategories && !showAllSubcategories
        ? subcategories.slice(0, subcategoryLimit)
        : subcategories;

    return (
      <>
        {/* Main categories */}
        <ul className={css.categoriesField}>
          {displayedMainCategories.map(item => (
            <li key={item.value}>
              <a
                href={createURL(item.value)}
                onClick={event => {
                  if (isModifierClick(event)) {
                    return;
                  }
                  event.preventDefault();

                  onNavigate(item.value);

                  const userID = currentUser?.id?.uuid;
                  if (userID && item.value) {
                    algoliaEventTrackFn(clickedFiltersMethodName, {
                      index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
                      eventName: FILTER_CLICKED_EVENT,
                      userToken: userID,
                      filters: [`${attribute}:${item.value}`],
                      eventData: {
                        filterValue: item.value,
                        userID,
                        timestamp: new Date().toISOString(),
                      },
                    });
                  }
                }}
                className={
                  item.isRefined || (item.data && item.data.some(sub => sub.isRefined))
                    ? css.refinedItem
                    : ''
                }
              >
                <span>{item.label}</span>
                <span> ({item.count})</span>
              </a>
            </li>
          ))}
        </ul>

        {/* See more/less button for main categories */}
        {shouldLimitMainCategories && (
          <div className={css.seeAllButtonWrapper}>
            <button className={css.seeAllButton} onClick={toggleShowAll}>
              {showAllSubcategories ? 'See less' : `See more (${sortedItems.length})`}
            </button>
          </div>
        )}

        {/* Subcategories at the bottom (only if a parent is selected) */}
        {selectedParent && subcategories.length > 0 && (
          <>
            <div className={css.subcategoryDivider}></div>
            <div className={css.subcategoryHeadings}>
              <span>{selectedParent.label}</span>
            </div>
            <ul className={css.categoriesField}>
              {displayedSubcategories
                .sort((a, b) => a.value.localeCompare(b.value))
                .map(subItem => (
                  <li key={subItem.value}>
                    <a
                      href={createURL(subItem.value)}
                      onClick={event => {
                        if (isModifierClick(event)) {
                          return;
                        }
                        event.preventDefault();

                        onNavigate(subItem.value);

                        const userID = currentUser?.id?.uuid;
                        if (userID && subItem.value) {
                          algoliaEventTrackFn(clickedFiltersMethodName, {
                            index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
                            eventName: FILTER_CLICKED_EVENT,
                            userToken: userID,
                            filters: [`${allAttribute[hierarchicalLevel]}:${subItem.value}`],
                            eventData: {
                              filterValue: subItem.value,
                              userID,
                              timestamp: new Date().toISOString(),
                            },
                          });
                        }
                      }}
                      className={subItem.isRefined ? css.refinedItem : ''}
                    >
                      <span>{subItem.label.replace(`${selectedParent.label} > `, '')}</span>
                      <span> ({subItem.count})</span>
                    </a>
                  </li>
                ))}
            </ul>
            {shouldLimitSubcategories && (
              <div className={css.seeAllButtonWrapper}>
                <button className={css.seeAllButton} onClick={toggleShowAll}>
                  {showAllSubcategories ? 'See less' : `See all (${subcategories.length})`}
                </button>
              </div>
            )}
          </>
        )}
      </>
    );
  }

  // Desktop layout: Show nested hierarchy
  return (
    <ul className={css.categoriesField}>
      {items
        .sort((a, b) => a.value.localeCompare(b.value))
        .map(item => {
          const hasSubcategories = item.data && item.data.length > 0;

          return (
            <li key={item.value}>
              <a
                href={createURL(item.value)}
                onClick={event => {
                  if (isModifierClick(event)) {
                    return;
                  }
                  event.preventDefault();

                  onNavigate(item.value);

                  const userID = currentUser?.id?.uuid;
                  if (userID && item.value) {
                    algoliaEventTrackFn(clickedFiltersMethodName, {
                      index: process.env.REACT_APP_ALGOLIA_LISTING_INDEX,
                      eventName: FILTER_CLICKED_EVENT,
                      userToken: userID,
                      filters: [`${attribute}:${item.value}`],
                      eventData: {
                        filterValue: item.value,
                        userID,
                        timestamp: new Date().toISOString(),
                      },
                    });
                  }
                }}
                className={item.isRefined ? css.refinedItem : ''}
              >
                <span>{item.label}</span>
                <span> ({item.count})</span>
              </a>
              {hasSubcategories && (
                <HierarchicalList
                  items={item.data}
                  onNavigate={onNavigate}
                  createURL={createURL}
                  currentUser={currentUser}
                  attribute={allAttribute[hierarchicalLevel]}
                  allAttribute={allAttribute}
                  level={hierarchicalNextLevel}
                  isMobileLayout={isMobileLayout}
                />
              )}
            </li>
          );
        })}
    </ul>
  );
}

const AlgoliaFilterPanel = ({
  Panel,
  isMobileLayout = true,
  showMoreFilters,
  setShowMoreFilters,
  currentUser,
  algoliaState,
  filterPanel = [],
  currentPage,
  categoryPageName,
  setAlgoliaState,
  onZipcodeSearch,
  searchZipcode,
  closeMobileFilters,
  history,
  location,
  onClearFilter,
  showheading = false,
}) => {
  const intl = useIntl();

  // intl.formatMessage({ id: 'Avatar.bannedUserDisplayName' })

  const getFilterComponent = item => {
    const { component } = item;
    return component == range_input ? (
      <PrinceRangeInput {...item} attribute={'price'} isMobileLayout={isMobileLayout} />
    ) : component == refinement_list ? (
      <RefinementList
        {...item}
        attribute={item.attribute}
        searchablePlaceholder={item.searchablePlaceholder}
        showMore={item.showMore}
      />
    ) : component == menu ? (
      <Menu {...item} attribute={item.attribute} showMore={item.showMore} />
    ) : component == menuSelect ? (
      <SelectFilter
        {...item}
        key={item.attribute}
        attribute={item.attribute}
        label={item.title}
        currentUser={currentUser}
        algoliaState={algoliaState}
      />
    ) : component == customRefinementList ? (
      <CustomRefinementList
        {...item}
        key={item.attribute}
        attribute={item.attribute}
        label={item.title}
        currentUser={currentUser}
        algoliaState={algoliaState}
        setAlgoliaState={setAlgoliaState}
        isMobileLayout={isMobileLayout}
        history={history}
        location={location}
        onClearFilter={onClearFilter}
      />
    ) : component == hierarchicalList ? (
      <CustomHierarchicalMenu
        {...item}
        key={'item.attribute'}
        attribute={item.attribute}
        label={item.title}
        currentUser={currentUser}
        algoliaState={algoliaState}
        setAlgoliaState={setAlgoliaState}
        categoryPageName={categoryPageName}
        isMobileLayout={isMobileLayout}
        history={history}
        location={location}
      />
    ) : component == zipcode_text_field ? (
      <ZipcodeSearchField
        {...item}
        key={item.attribute}
        label={item.title}
        isMobileLayout={isMobileLayout}
        algoliaState={algoliaState}
        setAlgoliaState={setAlgoliaState}
        onZipcodeSearch={onZipcodeSearch}
        initialZipcode={searchZipcode}
        closeMobileFilters={closeMobileFilters}
        history={history}
        location={location}
      />
    ) : null;
  };

  return (
    <>
      {!isMobileLayout && showheading ? <label className={css.pageHeading}>Directory</label> : null}
      {filterPanel
        .filter(st => isMobileLayout || !st.moreFilter)
        .map((item, index) => (
          <Panel key={item.title}>{getFilterComponent(item)}</Panel>
        ))}

      <Modal
        isOpen={showMoreFilters}
        onClose={() => setShowMoreFilters(false)}
        onManageDisableScrolling={() => {}}
        modalTitle="Filters"
        id="AlgoliaFilterPanel.filterModal"
        className={css.moreFilterModal}
      >
        <div>
          <h4>More Filters</h4>
          <div className={css.moreFilterModalContent}>
            {filterPanel
              .filter(st => st.moreFilter)
              .map((item, index) => (
                <Panel key={item.title}>{getFilterComponent(item)}</Panel>
              ))}
          </div>
        </div>
      </Modal>
    </>
  );
};

export default AlgoliaFilterPanel;
